import ApiService from './api.service';

// Mirrors MAX_ARTICLE_BODY_CHARS in src/models/knowledge_base_item.py — the API
// rejects article bodies over this size because long articles overload the AI
// assistant's context. See docs/knowledge_base_tools.md.
export const MAX_ARTICLE_BODY_CHARS = 20_000;

export type KnowledgeBaseItem = {
  id: string;
  item_type: 'folder' | 'article';
  name: string;
  parent_id: string | null;
  body?: string;
  tags: string[];
  status: 'active' | 'pending';
  proposed_by?: string | null;
  original_item_id?: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  last_updated: string;
};

export type SingleRecordResponse<T> = {
  record: T;
};

export type ListRecordsResponse<T> = {
  records: T[];
  total_records?: number;
  total_pages?: number;
};

export class KnowledgeBaseService {
  async getTree(): Promise<ListRecordsResponse<KnowledgeBaseItem>> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/knowledge-base-items/tree'
    ) as ListRecordsResponse<KnowledgeBaseItem>;
    return result;
  }

  async findById(id: string): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/knowledge-base-items/${id}`
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async create(item: Partial<KnowledgeBaseItem>): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/knowledge-base-items',
      item
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async save(item: Partial<KnowledgeBaseItem>): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/knowledge-base-items/${item.id}`,
      item
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/knowledge-base-items/${id}`
    );
  }

  async move(id: string, newParentId: string | null): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/${id}/move`,
      { new_parent_id: newParentId }
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async search(query: string): Promise<ListRecordsResponse<KnowledgeBaseItem>> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/knowledge-base-items/search',
      { q: query }
    ) as ListRecordsResponse<KnowledgeBaseItem>;
    return result;
  }

  async getTags(): Promise<string[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/knowledge-base-items/tags'
    ) as { tags: string[] };
    return result.tags;
  }

  /** 409s when the article changed after the update was proposed, unless `force`. */
  async approve(id: string, force = false): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/${id}/approve${force ? '?force=true' : ''}`
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async markPending(id: string): Promise<KnowledgeBaseItem> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/${id}/mark-pending`
    ) as SingleRecordResponse<KnowledgeBaseItem>;
    return result.record;
  }

  async reject(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/${id}/reject`
    );
  }
}

// ----- Consolidation types -----

export type KBConsolidationRunStatus =
  | 'pending'
  | 'ready'
  | 'applied'
  | 'rejected'
  | 'failed';

export type ArticleSnapshot = {
  article_id: string;
  name: string;
  body?: string | null;
  tags: string[];
  parent_id?: string | null;
  folder_path?: string | null;
};

export type ProposedMerge = {
  survivor_article_id: string;
  merged_article_ids: string[];
  new_name?: string | null;
  new_body?: string | null;
  new_tags?: string[] | null;
  rationale?: string | null;
};

export type ProposedDelete = {
  article_id: string;
  rationale?: string | null;
};

export type ProposedEdit = {
  article_id: string;
  new_name?: string | null;
  new_body?: string | null;
  new_tags?: string[] | null;
  rationale?: string | null;
};

export type ProposedMove = {
  article_id: string;
  new_parent_id?: string | null;
  rationale?: string | null;
};

export type ProposedFolderCreate = {
  temp_id: string;
  name: string;
  parent_id?: string | null;
  rationale?: string | null;
};

export type KBConsolidationRun = {
  id: string;
  status: KBConsolidationRunStatus;
  llm_model?: string | null;
  original_articles: ArticleSnapshot[];
  proposed_merges: ProposedMerge[];
  proposed_deletes: ProposedDelete[];
  proposed_edits: ProposedEdit[];
  proposed_moves: ProposedMove[];
  proposed_folder_creates: ProposedFolderCreate[];
  error?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  applied_at?: string | null;
};

export type ConsolidationExclude = {
  merges?: number[];
  deletes?: number[];
  edits?: number[];
  moves?: number[];
  folder_creates?: number[];
};

type KBConsolidationRunResponse = { record: KBConsolidationRun };

export class KBConsolidationService {
  async startConsolidation(force = false): Promise<KBConsolidationRun> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/consolidate${force ? '?force=true' : ''}`
    ) as KBConsolidationRunResponse;
    return result.record;
  }

  async getConsolidationRun(runId: string): Promise<KBConsolidationRun> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/knowledge-base-items/consolidation-runs/${runId}`
    ) as KBConsolidationRunResponse;
    return result.record;
  }

  async applyConsolidation(
    runId: string,
    exclude?: ConsolidationExclude
  ): Promise<KBConsolidationRun> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/consolidation-runs/${runId}/apply`,
      exclude ? { exclude } : undefined
    ) as KBConsolidationRunResponse;
    return result.record;
  }

  async rejectConsolidation(runId: string): Promise<KBConsolidationRun> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/knowledge-base-items/consolidation-runs/${runId}/reject`
    ) as KBConsolidationRunResponse;
    return result.record;
  }
}

export const knowledgeBaseService = new KnowledgeBaseService();
export const kbConsolidationService = new KBConsolidationService();
